import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatearCLP, formatearPorcentajeSimple } from '@lexicore/shared';
import {
  IconoAlerta,
  IconoBasura,
  IconoBuscar,
  IconoCerrar,
  IconoCheck,
  IconoCuadricula,
  IconoDerecha,
  IconoEditar,
  IconoIzquierda,
  IconoMas,
  IconoTabla,
} from '../componentes/Iconos';
import {
  BarraAvance,
  BarraPresupuesto,
  Cargando,
  ErrorCarga,
  Insignia,
  Panel,
  TarjetaKpi,
  Vacio,
} from '../componentes/UI';
import { useAvisos } from '../contextos/AvisosContext';
import {
  useCategorias,
  useEstadisticasCartera,
  useOrganismos,
  useProyectosPaginados,
  type Proyecto,
  type SaludProyecto,
} from '../lib/consultas';
import { useCambiarEstadoProyecto } from '../lib/mutaciones';
import { ModalCrearProyecto } from './proyecto/ModalCrearProyecto';
import { ModalEditarProyecto } from './proyecto/ModalEditarProyecto';
import { ModalEliminarProyecto } from './proyecto/ModalEliminarProyecto';

export function Proyectos() {
  const navegar = useNavigate();
  const avisos = useAvisos();

  // ── Filtros y paginación ──
  const [pagina, setPagina] = useState(1);
  const [porPagina] = useState(12);
  const [busqueda, setBusqueda] = useState('');
  const [estado, setEstado] = useState<string>('TODOS');
  const [salud, setSalud] = useState<string>('TODOS');
  const [organismoId, setOrganismoId] = useState<string>('TODOS');
  const [categoriaId, setCategoriaId] = useState<string>('TODAS');
  const [filtroPlazo, setFiltroPlazo] = useState<'TODOS' | 'ATRASADOS' | 'PROXIMOS_30' | 'PROXIMOS_90'>('TODOS');
  const [soloEnRiesgo, setSoloEnRiesgo] = useState(false);
  const [ordenarPor, setOrdenarPor] = useState('fechaFinPlanificada');
  const [orden, setOrden] = useState<'asc' | 'desc'>('asc');
  const [vista, setVista] = useState<'cards' | 'tabla'>('cards');

  // ── Modales de Proyecto ──
  const [modalCrearAbierto, setModalCrearAbierto] = useState(false);
  const [proyectoEditarModal, setProyectoEditarModal] = useState<Proyecto | null>(null);
  const [proyectoEliminarModal, setProyectoEliminarModal] = useState<Proyecto | null>(null);
  const [proyectoEstadoModal, setProyectoEstadoModal] = useState<Proyecto | null>(null);
  const [nuevoEstado, setNuevoEstado] = useState<string>('');
  const [motivoEstado, setMotivoEstado] = useState<string>('');
  const cambiarEstadoMut = useCambiarEstadoProyecto();

  // ── Consultas Backend ──
  const statsQuery = useEstadisticasCartera();
  const organismosQuery = useOrganismos();
  const categoriasQuery = useCategorias();

  // Cálculo de fechas para filtro de plazo
  const fechasPlazo = useMemo(() => {
    if (filtroPlazo === 'PROXIMOS_30') {
      const hasta = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      return { fechaFinHasta: hasta };
    }
    if (filtroPlazo === 'PROXIMOS_90') {
      const hasta = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
      return { fechaFinHasta: hasta };
    }
    return {};
  }, [filtroPlazo]);

  const proyectosQuery = useProyectosPaginados({
    pagina,
    porPagina,
    busqueda: busqueda.trim() || undefined,
    estado: estado !== 'TODOS' ? estado : undefined,
    salud: salud !== 'TODOS' ? salud : undefined,
    organismoId: organismoId !== 'TODOS' ? organismoId : undefined,
    fechaFinHasta: fechasPlazo.fechaFinHasta,
    soloEnRiesgo: soloEnRiesgo || undefined,
    ordenarPor,
    orden,
  });

  const stats = statsQuery.data;
  const respuesta = proyectosQuery.data;
  const proyectosOriginales = respuesta?.datos ?? [];

  // Filtrado adicional en memoria para categoría y atrasados
  const proyectos = useMemo(() => {
    return proyectosOriginales.filter((p) => {
      if (categoriaId !== 'TODAS' && p.licitacion?.categoria?.id !== categoriaId) {
        return false;
      }
      if (filtroPlazo === 'ATRASADOS') {
        const dias = p.metricas.diasRestantes;
        if (dias === null || dias === undefined || dias >= 0) return false;
      }
      return true;
    });
  }, [proyectosOriginales, categoriaId, filtroPlazo]);

  const total = respuesta?.total ?? 0;
  const totalPaginas = respuesta?.totalPaginas ?? 1;

  // Cómputos para barra de resumen de filtros
  const hayFiltrosActivos =
    busqueda.trim() !== '' ||
    estado !== 'TODOS' ||
    salud !== 'TODOS' ||
    organismoId !== 'TODOS' ||
    categoriaId !== 'TODAS' ||
    filtroPlazo !== 'TODOS' ||
    soloEnRiesgo ||
    ordenarPor !== 'fechaFinPlanificada' ||
    orden !== 'asc';

  const limpiarFiltros = () => {
    setBusqueda('');
    setEstado('TODOS');
    setSalud('TODOS');
    setOrganismoId('TODOS');
    setCategoriaId('TODAS');
    setFiltroPlazo('TODOS');
    setSoloEnRiesgo(false);
    setOrdenarPor('fechaFinPlanificada');
    setOrden('asc');
    setPagina(1);
  };

  const totalMontoFiltrado = useMemo(() => {
    return proyectos.reduce((acc, p) => {
      const monto = p.modalidadMonto === 'BRUTO' && p.montoContratoBruto ? p.montoContratoBruto : p.montoContratoNeto;
      return acc + monto;
    }, 0);
  }, [proyectos]);

  // ── Guardar cambio de estado con auditoría ──
  const guardarCambioEstado = async () => {
    if (!proyectoEstadoModal || !nuevoEstado) return;
    try {
      await cambiarEstadoMut.mutateAsync({
        id: proyectoEstadoModal.id,
        estado: nuevoEstado,
        motivo: motivoEstado.trim() || undefined,
      });
      avisos.exito(`Estado cambiado a ${nuevoEstado}`);
      setProyectoEstadoModal(null);
      setNuevoEstado('');
      setMotivoEstado('');
    } catch (err) {
      avisos.error(err instanceof Error ? err.message : 'No se pudo actualizar el estado.');
    }
  };

  return (
    <>
      {/* ── Cabecera principal ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          marginBottom: 20,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, letterSpacing: '-0.01em' }}>
            Control y Gestión de Proyectos
          </h1>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Supervisión operativa, conciliación financiera y semáforo de salud en tiempo real.
          </p>
        </div>

        {/* Acciones de cabecera: Nuevo Proyecto + Conmutador de vista */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setModalCrearAbierto(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}
          >
            <IconoMas size={15} />
            <span>Nuevo Proyecto</span>
          </button>

          <div className="view-toggle-wrap">
            <button
              type="button"
              className={`view-toggle-btn${vista === 'cards' ? ' activo' : ''}`}
              onClick={() => setVista('cards')}
              aria-label="Vista de tarjetas"
            >
              <IconoCuadricula size={14} />
              <span>Tarjetas</span>
            </button>
            <button
              type="button"
              className={`view-toggle-btn${vista === 'tabla' ? ' activo' : ''}`}
              onClick={() => setVista('tabla')}
              aria-label="Vista de tabla"
            >
              <IconoTabla size={14} />
              <span>Tabla</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── KPIs Ejecutivos de Cartera ── */}
      {stats && (
        <>
          <div className="kpi-grid" style={{ marginBottom: 16 }}>
            <TarjetaKpi
              etiqueta="En Ejecución"
              valor={stats.porEstado.enEjecucion}
              acento="var(--accent-blue)"
              colorValor="var(--accent-blue)"
              nota={`${stats.porEstado.planificados} planificados · ${stats.porEstado.cerrados} cerrados`}
            />
            <TarjetaKpi
              etiqueta="Salud de Cartera"
              valor={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ color: 'var(--accent-green)' }}>{stats.porSalud.sanos}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>/</span>
                  <span style={{ color: 'var(--accent-yellow)' }}>{stats.porSalud.alerta}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: 16 }}>/</span>
                  <span style={{ color: 'var(--accent-red)' }}>{stats.porSalud.criticos}</span>
                </span>
              }
              acento={stats.porSalud.criticos > 0 ? 'var(--accent-red)' : stats.porSalud.alerta > 0 ? 'var(--accent-yellow)' : 'var(--accent-green)'}
              nota="🟢 Sanos · 🟡 En Alerta · 🔴 Críticos"
            />
            <TarjetaKpi
              etiqueta="Valor Cartera Contratada"
              valor={formatearCLP(stats.financiero.valorCarteraContratadoNeto)}
              acento="var(--accent-green)"
              colorValor="var(--accent-green)"
              nota={`Costos ejecutados: ${formatearCLP(stats.financiero.costoRealEjecutadoNeto)}`}
            />
            <TarjetaKpi
              etiqueta="Utilidad Real Acumulada"
              valor={formatearCLP(stats.financiero.utilidadRealAcumuladaNeto)}
              acento="var(--accent-purple)"
              colorValor={stats.financiero.utilidadRealAcumuladaNeto >= 0 ? 'var(--accent-purple)' : 'var(--accent-red)'}
              nota={`${formatearPorcentajeSimple(stats.financiero.margenRealPonderadoPct)} margen ponderado`}
            />
          </div>

          {/* ── Conciliación Financiera y Cobranza (Sección 2.1 del Contrato) ── */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.08) 100%)',
              border: '1px solid rgba(59, 130, 246, 0.2)',
              borderRadius: 'var(--radius-lg)',
              padding: '14px 18px',
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--accent-blue)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 16,
                }}
              >
                $
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-primary)' }}>
                  Conciliación Financiera y Cobranza (Cartera Activa)
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>
                  Control de emisión de facturas y recaudación bancaria efectiva frente a clientes y Estado
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 24, alignItems: 'center', flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Total Facturado</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                  {formatearCLP(stats.financiero.totalFacturadoNeto)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Total Cobrado</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: 'var(--accent-green)' }}>
                  {formatearCLP(stats.financiero.totalCobradoNeto)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Saldo por Cobrar</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: stats.financiero.totalPorCobrarNeto > 0 ? 'var(--accent-yellow)' : 'var(--text-muted)' }}>
                  {formatearCLP(stats.financiero.totalPorCobrarNeto)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>Efectividad Cobro</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 14, color: 'var(--accent-blue)' }}>
                  {stats.financiero.totalFacturadoNeto > 0
                    ? `${Math.round((stats.financiero.totalCobradoNeto / stats.financiero.totalFacturadoNeto) * 100)}%`
                    : '100%'}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Alerta de Desviaciones Críticas ── */}
      {stats && stats.topDesviaciones && stats.topDesviaciones.length > 0 && (
        <div className="alerta-desviaciones">
          <div className="alerta-desviaciones-header">
            <div className="alerta-desviaciones-titulo">
              <IconoAlerta size={16} />
              <span>
                Atención requerida: {stats.proyectosEnRiesgoCount} proyecto(s) con desviaciones operativas o de presupuesto
              </span>
            </div>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setSoloEnRiesgo(true);
                setSalud('TODOS');
                setPagina(1);
              }}
            >
              Filtrar proyectos en riesgo
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 10 }}>
            {stats.topDesviaciones.slice(0, 3).map((desv) => (
              <div
                key={desv.id}
                onClick={() => navegar(`/proyectos/${desv.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navegar(`/proyectos/${desv.id}`); }}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid rgba(211, 47, 47, 0.25)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 14px',
                  cursor: 'pointer',
                  transition: '0.2s',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <strong style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{desv.nombre}</strong>
                  <span className={`badge-salud badge-salud-${desv.salud}`}>{desv.salud}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.4 }}>
                  {desv.motivoPrincipal}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Barra de herramientas, búsqueda y filtros 100% interactiva ── */}
      <div
        style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border-subtle)',
          borderRadius: 'var(--radius-lg)',
          padding: '16px',
          marginBottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {/* Fila 1: Búsqueda, Estado, Organismo, Categoría, Orden */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Buscador inteligente */}
          <div
            style={{
              position: 'relative',
              flex: '1 1 240px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <span style={{ position: 'absolute', left: 12, color: 'var(--text-muted)', pointerEvents: 'none' }}>
              <IconoBuscar size={14} />
            </span>
            <input
              type="text"
              value={busqueda}
              onChange={(e) => {
                setBusqueda(e.target.value);
                setPagina(1);
              }}
              placeholder="Buscar por nombre, código o cliente…"
              style={{
                width: '100%',
                padding: '8px 32px 8px 34px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12.5,
              }}
            />
            {busqueda && (
              <button
                type="button"
                onClick={() => {
                  setBusqueda('');
                  setPagina(1);
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  padding: 4,
                }}
                aria-label="Limpiar búsqueda"
              >
                <IconoCerrar size={12} />
              </button>
            )}
          </div>

          {/* Filtro por Estado */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Estado:
            </span>
            <select
              value={estado}
              onChange={(e) => {
                setEstado(e.target.value);
                setPagina(1);
              }}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12,
              }}
            >
              <option value="TODOS">Todos los estados</option>
              <option value="EN_EJECUCION">En ejecución</option>
              <option value="PLANIFICADO">Planificado</option>
              <option value="EN_PAUSA">En pausa</option>
              <option value="CERRADO">Cerrado</option>
              <option value="CANCELADO">Cancelado</option>
            </select>
          </div>

          {/* Filtro por Cliente / Organismo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Cliente:
            </span>
            <select
              value={organismoId}
              onChange={(e) => {
                setOrganismoId(e.target.value);
                setPagina(1);
              }}
              style={{
                maxWidth: 200,
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12,
              }}
            >
              <option value="TODOS">Todos los clientes</option>
              {organismosQuery.data?.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Filtro por Categoría / Línea de Negocio */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Categoría:
            </span>
            <select
              value={categoriaId}
              onChange={(e) => {
                setCategoriaId(e.target.value);
                setPagina(1);
              }}
              style={{
                maxWidth: 180,
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12,
              }}
            >
              <option value="TODAS">Todas las líneas</option>
              {categoriasQuery.data?.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nombre}
                </option>
              ))}
            </select>
          </div>

          {/* Ordenamiento */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
              Orden:
            </span>
            <select
              value={`${ordenarPor}_${orden}`}
              onChange={(e) => {
                const partes = e.target.value.split('_');
                const campo = partes[0] ?? 'fechaFinPlanificada';
                const direccion = (partes[1] as 'asc' | 'desc') ?? 'asc';
                setOrdenarPor(campo);
                setOrden(direccion);
                setPagina(1);
              }}
              style={{
                padding: '7px 10px',
                background: 'var(--bg-input)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
                fontSize: 12,
              }}
            >
              <option value="fechaFinPlanificada_asc">Plazo (más próximo)</option>
              <option value="fechaFinPlanificada_desc">Plazo (más lejano)</option>
              <option value="montoContratoNeto_desc">Monto contrato (mayor)</option>
              <option value="nombre_asc">Nombre (A - Z)</option>
              <option value="creadoEn_desc">Fecha creación (reciente)</option>
            </select>
          </div>
        </div>

        {/* Fila 2: Plazos, Salud Operativa, Solo en Riesgo, Botón Limpiar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingTop: 10, borderTop: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            {/* Filtro rápido por plazo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                Plazo:
              </span>
              <select
                value={filtroPlazo}
                onChange={(e) => {
                  setFiltroPlazo(e.target.value as any);
                  setPagina(1);
                }}
                style={{
                  padding: '6px 10px',
                  background: 'var(--bg-input)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--text-primary)',
                  fontSize: 12,
                }}
              >
                <option value="TODOS">Cualquier plazo</option>
                <option value="ATRASADOS">Solo atrasados</option>
                <option value="PROXIMOS_30">Próximos 30 días</option>
                <option value="PROXIMOS_90">Próximos 90 días</option>
              </select>
            </div>

            {/* Salud Operativa */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase' }}>
                Salud:
              </span>
              {(['TODOS', 'SANO', 'ALERTA', 'CRITICO'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`btn btn-sm ${salud === s ? 'btn-primary' : 'btn-secondary'}`}
                  style={{
                    padding: '3px 9px',
                    borderRadius: 20,
                    fontSize: 11,
                    background: salud === s ? undefined : 'var(--bg-elevated)',
                  }}
                  onClick={() => {
                    setSalud(s);
                    setPagina(1);
                  }}
                >
                  {s === 'TODOS' && 'Todos'}
                  {s === 'SANO' && '🟢 Sanos'}
                  {s === 'ALERTA' && '🟡 En Alerta'}
                  {s === 'CRITICO' && '🔴 Críticos'}
                </button>
              ))}
            </div>

            {/* Solo en riesgo */}
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              <input
                type="checkbox"
                checked={soloEnRiesgo}
                onChange={(e) => {
                  setSoloEnRiesgo(e.target.checked);
                  setPagina(1);
                }}
                style={{ accentColor: 'var(--accent-red)', width: 15, height: 15 }}
              />
              <span>Solo en riesgo</span>
            </label>
          </div>

          {hayFiltrosActivos && (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={limpiarFiltros}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5 }}
            >
              <IconoCerrar size={12} />
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        {/* Resumen dinámico del filtro */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, fontSize: 12, paddingTop: 4 }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Mostrando <strong>{proyectos.length}</strong> de {total} proyectos
            {hayFiltrosActivos && ' (filtros aplicados)'}
          </span>
          <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
            Cartera visible:{' '}
            <span style={{ color: 'var(--accent-green)', fontFamily: 'var(--font-mono)' }}>
              {formatearCLP(totalMontoFiltrado)}
            </span>
          </span>
        </div>
      </div>

      {/* ── Contenido de Proyectos (Loading / Error / Empty / List) ── */}
      {proyectosQuery.isLoading ? (
        <Cargando texto="Cargando proyectos…" />
      ) : proyectosQuery.isError ? (
        <ErrorCarga error={proyectosQuery.error} reintentar={() => void proyectosQuery.refetch()} />
      ) : proyectos.length === 0 ? (
        <Panel>
          <Vacio
            titulo="No se encontraron proyectos"
            detalle={
              soloEnRiesgo
                ? 'Ningún proyecto coincide con los filtros de riesgo seleccionados.'
                : busqueda
                ? `No hay proyectos que coincidan con "${busqueda}". Prueba con otro término.`
                : 'No hay proyectos registrados en este estado.'
            }
            accion={
              (busqueda || estado !== 'TODOS' || salud !== 'TODOS' || soloEnRiesgo) ? (
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => {
                    setBusqueda('');
                    setEstado('TODOS');
                    setSalud('TODOS');
                    setSoloEnRiesgo(false);
                    setPagina(1);
                  }}
                >
                  Restablecer filtros
                </button>
              ) : undefined
            }
          />
        </Panel>
      ) : vista === 'cards' ? (
        /* ── Vista Cuadrícula (Cards) ── */
        <div className="proyectos-grid">
          {proyectos.map((proyecto) => (
            <TarjetaProyectoItem
              key={proyecto.id}
              proyecto={proyecto}
              alAbrir={() => navegar(`/proyectos/${proyecto.id}`)}
              alCambiarEstado={() => {
                setProyectoEstadoModal(proyecto);
                setNuevoEstado(proyecto.estado);
                setMotivoEstado('');
              }}
              alEditar={() => setProyectoEditarModal(proyecto)}
              alEliminar={() => setProyectoEliminarModal(proyecto)}
            />
          ))}
        </div>
      ) : (
        /* ── Vista Tabla Ejecutiva ── */
        <div className="tabla-wrap">
          <div className="tabla-scroll">
            <table>
              <thead>
                <tr>
                  <th>Código & Proyecto</th>
                  <th>Cliente / Organismo</th>
                  <th>Estado</th>
                  <th>Salud</th>
                  <th style={{ textAlign: 'right' }}>Contrato</th>
                  <th style={{ textAlign: 'right' }}>Costo Real</th>
                  <th style={{ textAlign: 'center' }}>Presupuesto</th>
                  <th>Plazo</th>
                  <th style={{ textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {proyectos.map((p) => {
                  const saludP = p.metricas.salud ?? (p.metricas.alertas.some((a) => a.nivel === 'critico') ? 'CRITICO' : p.metricas.alertas.length > 0 ? 'ALERTA' : 'SANO');
                  const consumo = p.metricas.consumoPresupuestoPct;
                  return (
                    <tr key={p.id} style={{ cursor: 'pointer' }} onClick={() => navegar(`/proyectos/${p.id}`)}>
                      <td>
                        <div style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{p.nombre}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{p.codigo}</div>
                      </td>
                      <td>
                        <div style={{ color: 'var(--text-primary)', fontSize: 12 }}>
                          {p.licitacion?.organismo.nombre ?? p.organismo?.nombre ?? 'Sin organismo'}
                        </div>
                        {p.licitacion?.categoria && (
                          <span className="badge badge-cat" style={{ marginTop: 2 }}>{p.licitacion.categoria.nombre}</span>
                        )}
                      </td>
                      <td>
                        <Insignia estado={p.estado} />
                      </td>
                      <td>
                        <span className={`badge-salud badge-salud-${saludP}`}>
                          {saludP === 'SANO' && <IconoCheck size={11} />}
                          {saludP === 'ALERTA' && <IconoAlerta size={11} />}
                          {saludP === 'CRITICO' && <IconoAlerta size={11} />}
                          <span>{saludP}</span>
                        </span>
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right' }}>
                        <div>
                          {formatearCLP(
                            p.modalidadMonto === 'BRUTO' && p.montoContratoBruto
                              ? p.montoContratoBruto
                              : p.montoContratoNeto,
                          )}
                        </div>
                        {p.modalidadMonto === 'BRUTO' && p.montoContratoBruto ? (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            Neto: {formatearCLP(p.montoContratoNeto)}
                          </div>
                        ) : null}
                      </td>
                      <td className="td-mono" style={{ textAlign: 'right' }}>{formatearCLP(p.metricas.costoReal)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: consumo >= 100 ? 'var(--accent-red)' : consumo >= 80 ? 'var(--accent-yellow)' : 'var(--accent-green)',
                          }}
                        >
                          {p.presupuestoCostoPlanificado > 0 ? formatearPorcentajeSimple(consumo) : 'sin plan'}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                          {p.fechaFinPlanificada ? p.fechaFinPlanificada.slice(0, 10) : 'Sin plazo'}
                        </div>
                        {p.metricas.diasRestantes !== null && (
                          <div style={{ fontSize: 10, color: p.metricas.diasRestantes < 0 ? 'var(--accent-red)' : 'var(--text-muted)' }}>
                            {p.metricas.diasRestantes < 0 ? `Atrasado ${Math.abs(p.metricas.diasRestantes)}d` : `${p.metricas.diasRestantes}d restantes`}
                          </div>
                        )}
                      </td>
                      <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setProyectoEditarModal(p)}
                            title="Editar proyecto"
                            style={{ padding: '4px 7px' }}
                          >
                            <IconoEditar size={12} />
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => {
                              setProyectoEstadoModal(p);
                              setNuevoEstado(p.estado);
                              setMotivoEstado('');
                            }}
                            style={{ fontSize: 11, padding: '4px 8px' }}
                          >
                            Estado
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setProyectoEliminarModal(p)}
                            title="Eliminar proyecto"
                            style={{ padding: '4px 7px', color: 'var(--accent-red)' }}
                          >
                            <IconoBasura size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Paginación táctil amigable ── */}
      {total > 0 && (
        <div className="paginacion-touch">
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            Mostrando <strong>{proyectos.length}</strong> de <strong>{total}</strong> proyecto(s) · Página {pagina} de {totalPaginas}
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              type="button"
              className="btn btn-secondary paginacion-btn"
              disabled={pagina <= 1 || proyectosQuery.isFetching}
              onClick={() => setPagina((p) => Math.max(1, p - 1))}
              aria-label="Página anterior"
            >
              <IconoIzquierda size={14} />
              <span>Anterior</span>
            </button>
            <span style={{ fontSize: 12, fontWeight: 700, padding: '0 8px', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
              {pagina} / {totalPaginas}
            </span>
            <button
              type="button"
              className="btn btn-secondary paginacion-btn"
              disabled={pagina >= totalPaginas || proyectosQuery.isFetching}
              onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
              aria-label="Página siguiente"
            >
              <span>Siguiente</span>
              <IconoDerecha size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── Modal para Cambio de Estado de Proyecto con Motivo de Auditoría ── */}
      {proyectoEstadoModal && (
        <div className="modal-overlay" onClick={() => setProyectoEstadoModal(null)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Cambiar estado del proyecto</h2>
              <button
                type="button"
                className="btn-icono"
                onClick={() => setProyectoEstadoModal(null)}
                aria-label="Cerrar modal"
              >
                <IconoCerrar size={14} />
              </button>
            </div>

            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <strong style={{ fontSize: 14, color: 'var(--text-primary)' }}>
                  {proyectoEstadoModal.nombre}
                </strong>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {proyectoEstadoModal.codigo} · Estado actual: <Insignia estado={proyectoEstadoModal.estado} />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="selectNuevoEstado">Nuevo estado</label>
                <select
                  id="selectNuevoEstado"
                  value={nuevoEstado}
                  onChange={(e) => setNuevoEstado(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="EN_EJECUCION">En ejecución</option>
                  <option value="EN_PAUSA">En pausa</option>
                  <option value="PLANIFICADO">Planificado</option>
                  <option value="CERRADO">Cerrado</option>
                  <option value="CANCELADO">Cancelado</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="textareaMotivo">
                  Motivo del cambio (auditoría obligatoria para trazabilidad)
                </label>
                <textarea
                  id="textareaMotivo"
                  value={motivoEstado}
                  onChange={(e) => setMotivoEstado(e.target.value)}
                  rows={3}
                  placeholder="Ej: Aprobación de ampliación de plazo por parte del organismo, o pausa por tramitación de permisos."
                  style={{ width: '100%', resize: 'vertical' }}
                />
              </div>

              {nuevoEstado === 'EN_PAUSA' && (
                <div
                  style={{
                    padding: '10px 14px',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--accent-yellow-soft)',
                    color: 'var(--accent-yellow)',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  Pausar el proyecto suspende las alertas temporales hasta su reactivación.
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setProyectoEstadoModal(null)}
                disabled={cambiarEstadoMut.isPending}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void guardarCambioEstado()}
                disabled={cambiarEstadoMut.isPending || nuevoEstado === proyectoEstadoModal.estado}
              >
                {cambiarEstadoMut.isPending ? 'Guardando…' : 'Confirmar cambio'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de Edición de Proyecto ── */}
      {proyectoEditarModal && (
        <ModalEditarProyecto
          proyecto={proyectoEditarModal}
          alCerrar={() => setProyectoEditarModal(null)}
          alGuardarExitoso={() => {
            void proyectosQuery.refetch();
            void statsQuery.refetch();
          }}
        />
      )}

      {/* ── Modal de Eliminación de Proyecto ── */}
      {proyectoEliminarModal && (
        <ModalEliminarProyecto
          proyecto={{
            id: proyectoEliminarModal.id,
            codigo: proyectoEliminarModal.codigo,
            nombre: proyectoEliminarModal.nombre,
          }}
          alCerrar={() => setProyectoEliminarModal(null)}
          alEliminarExitoso={() => {
            void proyectosQuery.refetch();
            void statsQuery.refetch();
          }}
        />
      )}

      {/* ── Modal de Creación de Proyecto ── */}
      {modalCrearAbierto && (
        <ModalCrearProyecto
          alCerrar={() => setModalCrearAbierto(false)}
          alCrearExitoso={() => {
            void proyectosQuery.refetch();
            void statsQuery.refetch();
          }}
        />
      )}
    </>
  );
}

// ── Tarjeta individual de Proyecto con Semáforo y Acciones Rápidas ──
function TarjetaProyectoItem({
  proyecto,
  alAbrir,
  alCambiarEstado,
  alEditar,
  alEliminar,
}: {
  proyecto: Proyecto;
  alAbrir: () => void;
  alCambiarEstado: () => void;
  alEditar: () => void;
  alEliminar: () => void;
}) {
  const { metricas } = proyecto;
  const esActivo = proyecto.estado === 'EN_EJECUCION' || proyecto.estado === 'PLANIFICADO';
  const consumo = metricas.consumoPresupuestoPct;
  const salud: SaludProyecto =
    proyecto.metricas.salud ??
    (metricas.alertas.some((alerta) => alerta.nivel === 'critico')
      ? 'CRITICO'
      : metricas.alertas.length > 0
      ? 'ALERTA'
      : 'SANO');

  return (
    <article
      className="proyecto-card"
      onClick={alAbrir}
      role="button"
      tabIndex={0}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          alAbrir();
        }
      }}
    >
      {/* Cabecera de tarjeta: Nombre y badges */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
        <h4 style={{ fontSize: 13.5, fontWeight: 700, lineHeight: 1.35, flex: 1, color: 'var(--text-primary)' }}>
          {proyecto.nombre}
        </h4>
        <span className={`badge-salud badge-salud-${salud}`} title={metricas.motivosSalud?.join(' · ') || `Salud: ${salud}`}>
          {salud === 'SANO' && <IconoCheck size={11} />}
          {salud === 'ALERTA' && <IconoAlerta size={11} />}
          {salud === 'CRITICO' && <IconoAlerta size={11} />}
          {salud}
        </span>
      </div>

      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{proyecto.codigo}</span>
        <span>·</span>
        <span>{proyecto.licitacion?.organismo.nombre ?? proyecto.organismo?.nombre ?? 'Sin organismo'}</span>
        {proyecto.licitacion?.categoria && <span className="badge badge-cat">{proyecto.licitacion.categoria.nombre}</span>}
      </div>

      {/* Cifras Financieras */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        <div className="proyecto-meta">
          <span>{proyecto.modalidadMonto === 'BRUTO' && proyecto.montoContratoBruto ? 'Contrato (IVA incl.)' : 'Contrato neto'}</span>
          <strong>
            {formatearCLP(
              proyecto.modalidadMonto === 'BRUTO' && proyecto.montoContratoBruto
                ? proyecto.montoContratoBruto
                : proyecto.montoContratoNeto,
            )}
          </strong>
        </div>
        <div className="proyecto-meta">
          <span>Costos registrados</span>
          <strong style={{ color: metricas.costoReal > proyecto.presupuestoCostoPlanificado && proyecto.presupuestoCostoPlanificado > 0 ? 'var(--accent-red)' : undefined }}>
            {formatearCLP(metricas.costoReal)}
          </strong>
        </div>
        <div className="proyecto-meta">
          <span>Utilidad acumulada</span>
          <strong style={{ color: metricas.utilidad >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>
            {formatearCLP(metricas.utilidad)}
          </strong>
        </div>
      </div>

      {/* Barra de Plazo */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-muted)', marginBottom: 4 }}>
        <span>{esActivo ? 'Avance del plazo' : 'Plazo concluido'}</span>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{formatearPorcentajeSimple(metricas.avancePct)}</span>
      </div>
      <BarraAvance porcentaje={metricas.avancePct} enRiesgo={esActivo && metricas.diasRestantes !== null && metricas.diasRestantes < 30} />

      {/* Barra de Presupuesto */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10.5, color: 'var(--text-muted)', margin: '10px 0 4px' }}>
        <span>Presupuesto de costos</span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontWeight: 600,
            color: consumo >= 100 ? 'var(--accent-red)' : consumo >= 80 ? 'var(--accent-yellow)' : 'var(--accent-green)',
          }}
        >
          {proyecto.presupuestoCostoPlanificado > 0 ? formatearPorcentajeSimple(consumo) : 'sin plan'}
        </span>
      </div>
      <BarraPresupuesto porcentaje={proyecto.presupuestoCostoPlanificado > 0 ? consumo : 0} />

      {/* Pie de tarjeta con Estado y Acciones Rápidas */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 14,
          paddingTop: 10,
          borderTop: '1px solid var(--border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Insignia estado={proyecto.estado} />
          {esActivo && metricas.diasRestantes !== null && metricas.diasRestantes < 0 && (
            <span style={{ fontSize: 10.5, color: 'var(--accent-red)', fontWeight: 700 }}>
              {Math.abs(metricas.diasRestantes)}d atraso
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              alEditar();
            }}
            title="Editar proyecto"
            style={{ padding: '4px 7px' }}
          >
            <IconoEditar size={12} />
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              alCambiarEstado();
            }}
            style={{ fontSize: 11, padding: '4px 8px' }}
          >
            Estado
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={(e) => {
              e.stopPropagation();
              alEliminar();
            }}
            title="Eliminar proyecto"
            style={{ padding: '4px 7px', color: 'var(--accent-red)' }}
          >
            <IconoBasura size={12} />
          </button>
        </div>
      </div>

      {/* Alertas textuales si existen */}
      {metricas.alertas.length > 0 && (
        <div
          className="proyecto-alerta"
          style={{
            marginTop: 10,
            color: salud === 'CRITICO' ? 'var(--accent-red)' : 'var(--accent-yellow)',
          }}
        >
          <IconoAlerta size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>{metricas.alertas.map((alerta) => alerta.texto).join(' · ')}</span>
        </div>
      )}
    </article>
  );
}
